import { ApiBusiness } from '../services/api';

export type ContactLink = {
  key: string;
  label: string;
  href: string;
  kind: 'phone' | 'email' | 'website' | 'social';
  typeName: string;
  icon?: string;
  /** Phone messaging extras from API (whatsapp/signal/threema). */
  phoneExtras?: Array<{ name?: string; selected?: number }>;
};

function cleanHandle(value: string): string {
  return value.replace(/^@/, '').trim();
}

function ensureHttps(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

export function resolveSocialHref(typeName: string, raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (/^(https?|mailto|tel):/i.test(value)) return value;

  const t = typeName.toLowerCase();
  const handle = cleanHandle(value);

  if (t.includes('telegram') || value.includes('t.me')) {
    return `https://t.me/${handle.replace(/^https?:\/\/t\.me\//i, '')}`;
  }
  if (t.includes('instagram') || value.includes('instagram.com')) {
    return `https://instagram.com/${handle.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')}`;
  }
  if (t.includes('facebook') || value.includes('facebook.com') || value.includes('fb.com')) {
    if (value.includes('facebook.com') || value.includes('fb.com')) return ensureHttps(value);
    return `https://facebook.com/${handle}`;
  }
  if (t.includes('linkedin') || value.includes('linkedin.com')) {
    if (value.includes('linkedin.com')) return ensureHttps(value);
    return `https://linkedin.com/in/${handle}`;
  }
  if (t.includes('tiktok') || value.includes('tiktok.com')) {
    if (value.includes('tiktok.com')) return ensureHttps(value);
    return `https://tiktok.com/@${handle}`;
  }
  if (t.includes('youtube') || value.includes('youtube.com') || value.includes('youtu.be')) {
    if (value.includes('youtube.com') || value.includes('youtu.be')) return ensureHttps(value);
    return `https://youtube.com/@${handle}`;
  }
  if (t.includes('twitter') || t.includes('x.com') || value.includes('twitter.com') || value.includes('x.com')) {
    if (value.includes('twitter.com') || value.includes('x.com')) return ensureHttps(value);
    return `https://x.com/${handle}`;
  }
  if (t.includes('snapchat')) {
    return `https://www.snapchat.com/add/${handle}`;
  }
  if (t.includes('whatsapp')) {
    const digits = value.replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}` : ensureHttps(value);
  }
  if (t.includes('paypal')) {
    if (value.includes('paypal.') || value.includes('paypal.me')) return ensureHttps(value);
    return `https://paypal.me/${handle}`;
  }
  if (t.includes('twint')) {
    return value.startsWith('twint:') ? value : `tel:${value.replace(/\s/g, '')}`;
  }
  if (t.includes('website') || t.includes('web') || t.includes('http')) {
    return ensureHttps(value);
  }
  if (value.includes('.')) return ensureHttps(value);
  return value;
}

export function displaySocialLabel(typeName: string, raw: string): string {
  const value = raw.trim();
  const t = typeName.toLowerCase();
  if (t.includes('website') || t.includes('web')) return 'Website';
  if (value.startsWith('@')) return value;
  if (/^https?:\/\//i.test(value)) {
    try {
      const u = new URL(value);
      const path = u.pathname.replace(/\/$/, '');
      if (path && path !== '/') return `${u.hostname}${path}`;
      return u.hostname;
    } catch {
      return value;
    }
  }
  return value.startsWith('@') ? value : value;
}

export function buildContactLinks(business: ApiBusiness): ContactLink[] {
  const links: ContactLink[] = [];

  (business.phones || []).forEach((p, i) => {
    const phone = (p.phone || '').trim();
    if (!phone) return;
    links.push({
      key: `phone-${p.id ?? i}`,
      label: phone,
      href: `tel:${phone.replace(/\s/g, '')}`,
      kind: 'phone',
      typeName: 'Phone',
      phoneExtras: p.extras,
    });
  });

  (business.emails || []).forEach((e, i) => {
    const email = (e.email || '').trim();
    if (!email) return;
    links.push({
      key: `email-${e.id ?? i}`,
      label: email,
      href: `mailto:${email}`,
      kind: 'email',
      typeName: 'Email',
    });
  });

  const web = (business.web || '').trim();
  if (web) {
    links.push({
      key: 'website',
      label: displaySocialLabel('website', web),
      href: ensureHttps(web),
      kind: 'website',
      typeName: 'Website',
    });
  }

  const seen = new Set<string>();
  (business.socials || []).forEach((s, i) => {
    const typeName = s.type?.name || 'Link';
    // iOS stores the handle in `name`; older payloads may use url/social.
    const raw = (s.name || s.url || s.social || '').trim();
    if (!raw) return;
    const lower = typeName.toLowerCase();
    const preweb = (s.type?.preweb || '').trim();
    const postweb = (s.type?.postweb || '').trim();
    const handle = cleanHandle(raw);
    const href =
      (lower === 'spotify' || (lower.includes('spotify') && lower.includes('artist'))) &&
      /^[A-Za-z0-9]+$/.test(handle)
        ? `https://open.spotify.com/artist/${handle}`
        : preweb
          ? `${preweb}${encodeURIComponent(handle).replace(/%40/g, '@')}${postweb}`
          : resolveSocialHref(typeName, raw);
    if (!href) return;
    const dedupe = `${lower}:${href}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    // Avoid duplicating website already added from business.web
    if (web && (lower.includes('website') || lower.includes('web'))) {
      const webHref = ensureHttps(web);
      if (href === webHref || href === ensureHttps(raw)) return;
    }
    links.push({
      key: `social-${s.id ?? i}`,
      label: displaySocialLabel(typeName, raw),
      href,
      kind: 'social',
      typeName,
      icon: s.type?.icon,
    });
  });

  return links;
}
